import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
  useRef,
  useCallback,
} from "react";
import InactivityModal from "../InactivityModal";
import { refreshAccessToken } from "../../services/api/authHandler";


// Define the shape of your user object
interface User {
  sub: string;
  username: string;
  fullname: string;
  phonenumber: string;
  organizationid: number;
  created_at: string;
  role: string;
}

// Extend the AuthContextType to include the user object
interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  login: (userData: User) => void;
  logout: () => void;
}

// Create the AuthContext with a default value
const AuthContext = createContext<AuthContextType>(null!);

interface AuthProviderProps {
  children: ReactNode;
}

// Show the inactivity warning this far before the token's actual expiry.
const WARN_BEFORE_EXPIRY_SECONDS = 3 * 60;

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);

  // Inactivity-modal state. `tokenExp` is the unix-seconds moment the
  // current access token actually expires (countdown target inside the
  // modal). `showWarning` controls modal visibility. `isRefreshing`
  // disables the buttons while the refresh request is in flight.
  const [showWarning, setShowWarning] = useState(false);
  const [tokenExp, setTokenExp] = useState<number | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const warnTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const login = (userData: User) => {
    setUser(userData);
    setIsAuthenticated(true);
    localStorage.setItem("user", JSON.stringify(userData));
  };

  const logout = useCallback(async () => {
    try {
      const response = await fetch("/api/v1/logout", {
        method: "GET",
        credentials: "include",
      });
      if (response.ok) {
        setIsAuthenticated(false);
        setUser(null);
        localStorage.removeItem("user");
        setShowWarning(false);
        setTokenExp(null);
      } else {
        console.error("Logout failed");
      }
    } catch (error) {
      console.error("An error occurred during logout:", error);
    }
  }, []);

  // Verify the session and set the user data if authenticated. ONLY clears
  // auth state on an explicit 401. Transient backend issues (deploys,
  // brief outages) are ignored so users don't get logged out for them.
  const verifySession = async () => {
    setIsLoading(true);
    try {
      const response = await fetch("/api/v1/verify-session", {
        credentials: "include",
      });

      if (response.status === 401) {
        setIsAuthenticated(false);
        setUser(null);
        localStorage.removeItem("user");
        return;
      }

      if (!response.ok) {
        console.warn(`verifySession got ${response.status} — treating as transient, not logging out`);
        return;
      }

      const data = await response.json();
      if (data.isAuthenticated) {
        setIsAuthenticated(true);
        setUser(data.user);
      }
    } catch (error) {
      console.warn("verifySession failed (likely transient):", error);
    } finally {
      setIsLoading(false);
    }
  };

  const checkCookie = (name: string) => {
    return document.cookie
      .split(";")
      .some((item) => item.trim().startsWith(name + "="));
  };

  const handleUnauthorized = () => {
    setIsAuthenticated(false);
    setUser(null);
    localStorage.removeItem("user");
    setShowWarning(false);
    setTokenExp(null);
  };

  // Read the current access token's exp from the non-httpOnly `token`
  // cookie that the backend sets alongside `authorization` on login and
  // refresh. Returns unix seconds or null.
  const readTokenExp = (): number | null => {
    const c = document.cookie
      .split("; ")
      .find((x) => x.startsWith("token="))
      ?.split("=")[1];
    if (!c) return null;
    const n = parseInt(c);
    return isNaN(n) ? null : n;
  };

  // Schedule the inactivity warning to fire WARN_BEFORE_EXPIRY_SECONDS
  // before the token's actual expiration. Reads the latest exp from the
  // cookie so a refresh that updates the cookie can be picked up via a
  // fresh call. Returns the timer so callers can clear it on cleanup.
  const scheduleWarning = useCallback(() => {
    if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
    const exp = readTokenExp();
    if (!exp) return;
    setTokenExp(exp);

    const warnAtMs = exp * 1000 - WARN_BEFORE_EXPIRY_SECONDS * 1000;
    const msUntilWarn = warnAtMs - Date.now();

    if (msUntilWarn <= 0) {
      // Token is already within the warning window — show it now.
      setShowWarning(true);
      return;
    }
    warnTimerRef.current = setTimeout(() => {
      setShowWarning(true);
    }, msUntilWarn);
  }, []);

  // User clicked "Stay logged in." Trade the refresh token for a fresh
  // access token, then reschedule the next warning based on the new exp.
  const handleStayLoggedIn = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const result = await refreshAccessToken();
      if (result.ok) {
        setShowWarning(false);
        // Cookie has been updated by the backend; reschedule reads it.
        scheduleWarning();
      } else {
        // Refresh failed — refresh token might be expired or revoked.
        // Logout for real.
        await logout();
      }
    } finally {
      setIsRefreshing(false);
    }
  }, [logout, scheduleWarning]);

  // Either the modal countdown ran out OR user clicked "Sign out now."
  const handleModalLogout = useCallback(async () => {
    setShowWarning(false);
    await logout();
  }, [logout]);

  useEffect(() => {
    if (checkCookie("token")) {
      verifySession();
    } else {
      setIsAuthenticated(false);
      setIsLoading(false);
    }
  }, []);

  // Whenever auth flips to true (login, or page load with valid session),
  // start the warning schedule. Cleanup clears the timer on logout/unmount.
  useEffect(() => {
    if (!isAuthenticated) {
      if (warnTimerRef.current) {
        clearTimeout(warnTimerRef.current);
        warnTimerRef.current = null;
      }
      return;
    }
    scheduleWarning();
    return () => {
      if (warnTimerRef.current) {
        clearTimeout(warnTimerRef.current);
        warnTimerRef.current = null;
      }
    };
  }, [isAuthenticated, scheduleWarning]);

  useEffect(() => {
    window.addEventListener("unauthorized", handleUnauthorized);
    return () => window.removeEventListener("unauthorized", handleUnauthorized);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, isAuthenticated, isLoading, login, logout }}
    >
      {children}
      <InactivityModal
        show={showWarning && isAuthenticated}
        expiresAt={tokenExp}
        onStayLoggedIn={handleStayLoggedIn}
        onLogout={handleModalLogout}
        isRefreshing={isRefreshing}
      />
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);
